import { lazy, Suspense, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useModals, useModal, MODAL_TYPES } from '../../store/ModalStore';
import PageLoadingFallback from './PageLoadingFallback';

const DepartmentModal = lazy(
  () => import('../dashboard/PeoplePage/components/DepartmentModal'),
);
const AddPersonModal = lazy(
  () => import('../dashboard/PeoplePage/components/AddPersonModal'),
);
const ConfirmationModal = lazy(() =>
  import('../../dialogs').then((module) => ({
    default: module.ConfirmationModal,
  })),
);
const FilterSheet = lazy(
  () => import('../dashboard/PeoplePage/components/FilterSheet'),
);
const CreateOrganizationModal = lazy(
  () => import('../dashboard/SuperAdminDashboard/CreateOrganizationModal'),
);
const FacilityModal = lazy(() => import('../dashboard/FacilityModal'));
const CategoryModal = lazy(
  () => import('../dashboard/resources/CategoryModal'),
);
const SubcategoryModal = lazy(
  () => import('../dashboard/resources/SubcategoryModal'),
);
const ResourceModal = lazy(
  () => import('../dashboard/resources/ResourceModal'),
);

// Modal component mapping
const MODAL_COMPONENTS = {
  [MODAL_TYPES.DEPARTMENT]: DepartmentModal,
  [MODAL_TYPES.ADD_PERSON]: AddPersonModal,
  [MODAL_TYPES.CONFIRMATION]: ConfirmationModal,
  [MODAL_TYPES.FILTER]: FilterSheet,
  [MODAL_TYPES.CREATE_ORGANIZATION]: CreateOrganizationModal,
  [MODAL_TYPES.FACILITY]: FacilityModal,
  [MODAL_TYPES.CATEGORY]: CategoryModal,
  [MODAL_TYPES.SUBCATEGORY]: SubcategoryModal,
  [MODAL_TYPES.RESOURCE]: ResourceModal,
  // Add more modal mappings here as needed
};

const ModalRenderer = () => {
  const modals = useModals();
  const { closeModal } = useModal();

  // Handle ESC key to close modals
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && modals.length > 0) {
        closeModal(); // Close topmost modal
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [modals.length, closeModal]);

  // Prevent body scroll when modals are open
  useEffect(() => {
    if (modals.length > 0) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [modals.length]);

  if (modals.length === 0) {
    return null;
  }

  return createPortal(
    <div className="modal-container">
      {modals.map((modal, index) => {
        const ModalComponent = MODAL_COMPONENTS[modal.type];

        if (!ModalComponent) {
          console.warn(`Modal component not found for type: ${modal.type}`);
          return null;
        }

        const isTopModal = index === modals.length - 1;
        const zIndex = 1000 + index * 10; // Stacking z-index

        return (
          <div key={modal.id} className="fixed inset-0" style={{ zIndex }}>
            {/* Backdrop - only show for top modal to avoid multiple overlays */}
            {isTopModal && (
              <div
                className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity"
                onClick={() => closeModal(modal.id)}
                aria-hidden="true"
              />
            )}

            {/* Modal Content */}
            <div className="fixed inset-0 flex items-center justify-center p-4">
              <Suspense
                fallback={
                  <PageLoadingFallback
                    label="Loading modal"
                    className="flex min-h-40 min-w-72 items-center justify-center rounded-lg bg-white p-6 shadow-xl"
                  />
                }
              >
                <ModalComponent
                  isOpen={true}
                  onClose={() => closeModal(modal.id)}
                  modalId={modal.id}
                  {...modal.props}
                />
              </Suspense>
            </div>
          </div>
        );
      })}
    </div>,
    document.body,
  );
};

export default ModalRenderer;
